import { useEffect, useState } from "react";
import { ImageIcon, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Renders a Google Street View (or Maps Static fallback) image of a property.
 *  Source of truth is the backend `/api/properties/image` proxy — the Google
 *  API key stays server-side.
 *
 *  Auth model: the backend endpoint requires authentication (Bearer header
 *  preferred, httpOnly cookie fallback). The browser CANNOT attach a Bearer
 *  header to a native `<img src>` request, and cookie auth has historically
 *  been unreliable on iOS Safari and across the prod cross-domain boundary
 *  (preview.emergentagent.com → realtyvaultmgr.com), where users would see
 *  "No image available" because the `<img>` request returned 401.
 *
 *  So we fetch the image bytes ourselves with the Bearer header (same auth
 *  as every other API call), convert the response to a blob, and use the
 *  resulting object URL as the `<img src>`. This is the same pattern the
 *  rest of the app uses for authenticated downloads.
 *
 *  Props: one of { pid, address } must be set.
 *  - pid:     uses cached lat/lng on the property doc (preferred)
 *  - address: debounced live preview for Add/Edit form
 */
export default function PropertyImage({
  pid,
  address,
  size = "600x300",
  className = "",
  rounded = "rounded-sm",
  alt = "Property",
  heading,      // optional live-preview override (0-359)
  source,       // optional live-preview override: "streetview" | "staticmap"
  refreshKey,   // bump to force cache-bust (e.g. after saving a new heading)
}) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [empty, setEmpty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setErr(false);

    // Build the canonical request URL
    const buildUrl = () => {
      const extra = [];
      if (heading !== undefined && heading !== null && heading !== "") extra.push(`heading=${encodeURIComponent(heading)}`);
      if (source) extra.push(`source=${encodeURIComponent(source)}`);
      if (refreshKey !== undefined && refreshKey !== null) extra.push(`r=${encodeURIComponent(refreshKey)}`);
      const extraQs = extra.length ? `&${extra.join("&")}` : "";
      if (pid) return `${BACKEND_URL}/api/properties/image?pid=${pid}&size=${encodeURIComponent(size)}${extraQs}`;
      if (address && address.trim().length >= 5) {
        return `${BACKEND_URL}/api/properties/image?address=${encodeURIComponent(address)}&size=${encodeURIComponent(size)}${extraQs}`;
      }
      return null;
    };

    // Reset existing state
    setObjectUrl(null);
    setEmpty(!pid && (!address || address.trim().length < 5));

    const url = buildUrl();
    if (!url) return undefined;

    // Debounce only for address-based previews (typing live), not for pid lookups
    const delay = pid ? 0 : 600;
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem("rv_token");
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (!res.ok) {
          setErr(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch {
        if (!cancelled) setErr(true);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [pid, address, size, heading, source, refreshKey]);

  const [w, h] = size.split("x").map((n) => Number(n));
  const aspect = w && h ? `${w} / ${h}` : undefined;

  if (empty && !objectUrl) {
    return (
      <div
        className={`${className} ${rounded} flex items-center justify-center bg-slate-100 border border-dashed border-slate-200 text-slate-400`}
        style={{ aspectRatio: aspect }}
        data-testid="property-image-empty"
      >
        <div className="flex flex-col items-center text-[11px] font-mono">
          <MapPin className="w-5 h-5 mb-1 opacity-50" />
          {address ? "Type full address…" : "No image"}
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div
        className={`${className} ${rounded} flex items-center justify-center bg-slate-100 border border-dashed border-slate-200 text-slate-400`}
        style={{ aspectRatio: aspect }}
        data-testid="property-image-error"
      >
        <div className="flex flex-col items-center text-[11px] font-mono">
          <ImageIcon className="w-5 h-5 mb-1 opacity-50" />
          No image available
        </div>
      </div>
    );
  }
  if (!objectUrl) {
    // Loading state — same empty container so layout doesn't jump
    return (
      <div
        className={`${className} ${rounded} bg-slate-100`}
        style={{ aspectRatio: aspect }}
        data-testid="property-image-loading"
      />
    );
  }
  return (
    <img
      src={objectUrl}
      alt={alt}
      loading="lazy"
      className={`${className} ${rounded} object-cover bg-slate-100`}
      style={{ aspectRatio: aspect }}
      onError={() => setErr(true)}
      data-testid="property-image"
    />
  );
}
